import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDownload } from "@fortawesome/pro-regular-svg-icons/faDownload";
import { faSpinner } from "@fortawesome/pro-regular-svg-icons/faSpinner";
import { toast } from "react-toastify";
import axios from "axios";

import { HydraTable, TimeAgo, SelectFilter } from "@peracto/peracto-ui";
import { useConfig } from "@peracto/peracto-config";

import { GET_LIST, useClient } from "@peracto/client";
import { IMPORT_TYPES } from "./index";

const UserImportList = () => {
  const { client } = useClient();
  const config = useConfig();
  const formatDate = config.get("user_format_date", config.get("format_date"));
  const API_URL = config.get("api");

  const [downloadingImport, setDownloadingImport] = useState(null);

  const importTypes = [...IMPORT_TYPES];

  const getLabel = (value) => {
    const found = importTypes.find((type) => type.value === value);
    return found ? found.label : "-";
  };

  const onDownloadImport = async (id) => {
    setDownloadingImport(id);

    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      };

      const { data } = await axios.get(
        `${API_URL}/imports/pre-signed-url/${id}`,
        {
          headers,
        }
      );

      const s3Url = data?.preSignedUrl;

      if (s3Url) {
        window.location = s3Url;
      }
    } catch (e) {
      console.error(e);
      toast.error(
        e?.error?.body?.hasOwnProperty("hydra:description")
          ? e.error.body["hydra:description"]
          : "Whoops, there was a problem..."
      );
    } finally {
      setDownloadingImport(null);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Import Type",
        accessor: "importType",
        Filter: (props) => (
          <SelectFilter label="Import Type" options={importTypes} {...props} />
        ),
        Cell: ({ cell: { value } }) => getLabel(value),
      },
      {
        Header: "Source Type",
        accessor: "sourceType",
      },
      {
        Header: "Processed",
        accessor: "processedCount",
      },
      {
        Header: "Errors",
        accessor: "errorCount",
      },
      {
        Header: "Total",
        accessor: "totalCount",
      },
      {
        Header: "Created By",
        accessor: "user.email",
        id: "createdBy",
        Cell: ({ cell: { value } }) => value || "-",
      },
      {
        Header: "Created",
        accessor: "createdAt",
        Cell: ({ cell: { value } }) => {
          const date = new Date(value);
          const validDate = !!date.getDate() === true;

          return value && validDate ? (
            <TimeAgo title={formatDate(date)} date={date} />
          ) : (
            "-"
          );
        },
      },
      {
        Header: "% Complete",
        id: "percentComplete",
        Cell: ({ row }) =>
          row.original.totalCount > 0
            ? `${Math.round(
                (row.original.processedCount / row.original.totalCount) * 100
              )}%`
            : "-",
      },
      {
        Header: "View Import",
        id: "viewImport",
        disableSorting: true,
        Cell: ({ row }) => {
          const id = row.original.originId;

          return <Link to={`/users/import/${id}`}>View Import</Link>;
        },
        selectable: false,
      },
      {
        Header: () => (
          <span className="inline-block w-full text-right">Download</span>
        ),
        id: "download",
        disableSorting: true,
        Cell: ({ row }) => {
          const id = row.original.originId;
          const downloadInitialised = downloadingImport === id;

          return (
            <div className="text-right">
              <button
                className="btn btn-sm btn-outline-primary"
                disabled={!!downloadingImport}
                onClick={(e) => {
                  e.preventDefault();
                  onDownloadImport(id);
                }}
              >
                <FontAwesomeIcon
                  icon={downloadInitialised ? faSpinner : faDownload}
                  {...(downloadInitialised && {
                    spin: true,
                  })}
                  className="mr-2"
                />
                Download
              </button>
            </div>
          );
        },
        selectable: false,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [downloadingImport]
  );

  const onFetchData = (options) => {
    // If no importType filter is selected, add default filters for user and babbage-accounts
    let filter = options.filter || {};

    if (!filter.importType || filter.importType === "") {
      filter = {
        ...filter,
        importType: ["user", "babbage_account"],
      };
    }

    return client(GET_LIST, "imports", {
      ...options,
      filter,
    });
  };

  return (
    <HydraTable
      columns={columns}
      onFetchData={onFetchData}
      fixedRowCount={10}
      noResultsMessage="No user data has been imported"
      nestedTable={true}
    />
  );
};

export default UserImportList;
